import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ModalExportarLista from '../components/common/ModalExportarLista';
import styles from './Dashboard.module.css';

const COLUNAS_EXPORT_VENCER = [
  { key: 'codigo_interno', label: 'Código' },
  { key: 'descricao',      label: 'Descrição',
    render: p => p.numero_lote ? `${p.descricao} (Lote ${p.numero_lote})` : p.descricao },
  { key: 'estoque_atual',  label: 'Qtd. Estoque',
    render: p => Number(p.estoque_atual || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 }) },
  { key: 'preco_venda',    label: 'Preço Unit.',
    render: p => formatBRL(p.preco_venda) },
  { key: 'data_vencimento', label: 'Vencimento',
    render: p => p.data_vencimento ? new Date(String(p.data_vencimento).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—' },
  { key: 'dias_para_vencer', label: 'Dias p/ vencer',
    render: p => { const d = parseInt(p.dias_para_vencer); return d < 0 ? `Vencido há ${Math.abs(d)}d` : `${d}d`; } },
];

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatNum = (v) => Number(v || 0).toLocaleString('pt-BR');

const CORES = ['#1565FF', '#27AE60', '#d97706', '#32B0FF', '#9c27b0', '#dc2626', '#0D47A1', '#65a30d'];
// Mesma paleta categórica fixa usada em Relatórios (skill de dataviz) — ordem
// fixa, nunca ciclada por dado, pra distinguir loja por loja nos gráficos.
const CORES_LOJA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

// Banner de Notas Técnicas SEFAZ pendentes com obrigatoriedade próxima/vencida.
// Perfis sem menu Administrativo recebem 403 na API → banner simplesmente não aparece.
function BannerNotasTecnicas() {
  const { data: alertas } = useQuery({
    queryKey: ['nt-alertas'],
    queryFn: () => api.get('/notas-tecnicas/alertas').then(r => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  if (!alertas || (!alertas.vencidas && !alertas.proximas)) return null;

  const vencida = alertas.vencidas > 0;
  const cor = vencida
    ? { bg: '#fee2e2', bd: '#fca5a5', fg: '#991b1b' }
    : { bg: '#fef3c7', bd: '#fcd34d', fg: '#92400e' };
  const lista = (alertas.criticas || [])
    .map(c => `${c.codigo} (${new Date(c.data_obrigatoriedade.slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR')})`)
    .join(', ');

  return (
    <Link to="/admin/notas-tecnicas" style={{ textDecoration: 'none' }}>
      <div style={{
        background: cor.bg, border: `1px solid ${cor.bd}`, color: cor.fg,
        borderRadius: 8, padding: '10px 14px', marginBottom: 14,
        fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
      }}>
        {vencida
          ? <>🚨 <strong>{alertas.vencidas}</strong> Nota(s) Técnica(s) da SEFAZ com obrigatoriedade <strong>VENCIDA</strong> e não implementada{alertas.proximas ? <> + {alertas.proximas} vencendo em até 30 dias</> : null}</>
          : <>⚠️ <strong>{alertas.proximas}</strong> Nota(s) Técnica(s) da SEFAZ com obrigatoriedade nos próximos 30 dias</>}
        {lista && <span style={{ fontWeight: 400 }}> — {lista}</span>}
        <span style={{ float: 'right', fontWeight: 400 }}>ver detalhes →</span>
      </div>
    </Link>
  );
}

function Tendencia({ pct }) {
  if (pct == null) return null;
  const subiu = pct >= 0;
  return (
    <span className={`${styles.tendencia} ${subiu ? styles.tendenciaAlta : styles.tendenciaBaixa}`}>
      {subiu ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}% vs. ontem
    </span>
  );
}

function CardKpi({ titulo, valor, sub, tendenciaPct, icone, cor, to, onClick }) {
  const conteudo = (
    <div className={styles.card} style={{ '--card-accent': cor }}>
      <div className={styles.cardTopo}>
        <span className={styles.cardIcone} style={{ background: `${cor}1a`, color: cor }}>{icone}</span>
        <p className={styles.cardTitulo}>{titulo}</p>
      </div>
      <p className={styles.cardValor}>{valor}</p>
      <div className={styles.cardRodape}>
        {sub && <span className={styles.cardSub}>{sub}</span>}
        <Tendencia pct={tendenciaPct} />
      </div>
    </div>
  );
  if (to) return <Link to={to} className={styles.cardLink}>{conteudo}</Link>;
  if (onClick) return (
    <button type="button" onClick={onClick} className={styles.cardLink}
      style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}>
      {conteudo}
    </button>
  );
  return conteudo;
}

function TooltipMoeda({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltipCustom}>
      {label && <p className={styles.tooltipLabel}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <strong>{formatBRL(p.value)}</strong></p>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { usuario } = useAuth();
  const podeConsolidar = ['admin', 'gerente'].includes((usuario?.perfil || '').toLowerCase());
  const [consolidar, setConsolidar] = useState(false);
  const [modalVencer, setModalVencer] = useState(false);

  const { data: resumo } = useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: () => api.get('/dashboard/resumo').then((r) => r.data),
  });

  const { data: vendasDia = [] } = useQuery({
    queryKey: ['vendas-por-dia', podeConsolidar && consolidar],
    queryFn: () => api.get('/dashboard/vendas-por-dia', {
      params: { consolidar: podeConsolidar && consolidar ? 'true' : 'false' },
    }).then((r) => r.data),
  });

  const { data: topProdutos = [] } = useQuery({
    queryKey: ['top-produtos'],
    queryFn: () => api.get('/dashboard/top-produtos').then((r) => r.data),
  });

  const { data: topProdutosValor = [] } = useQuery({
    queryKey: ['dashboard-top-produtos-valor'],
    queryFn: () => api.get('/dashboard/top-produtos-valor').then((r) => r.data),
  });

  const { data: topClientes = [] } = useQuery({
    queryKey: ['dashboard-top-clientes'],
    queryFn: () => api.get('/dashboard/top-clientes').then((r) => r.data),
  });

  const { data: formasPagamento = [] } = useQuery({
    queryKey: ['dashboard-formas-pagamento'],
    queryFn: () => api.get('/dashboard/formas-pagamento').then((r) => r.data),
  });

  const { data: vendedores = [] } = useQuery({
    queryKey: ['dashboard-vendedores'],
    queryFn: () => api.get('/dashboard/vendedores').then((r) => r.data),
  });

  // Barra horizontal: o 1º item do array vira o de CIMA — os endpoints já vêm
  // ordenados do maior pro menor, então NÃO reverter aqui (senão a leitura
  // fica de baixo pra cima).
  const nomeCurto = (n, tam = 26) => n.length > tam ? n.slice(0, tam - 2) + '…' : n;
  const vendasDiaFmt = vendasDia.map(d => ({ ...d, total: parseFloat(d.total) || 0 }));

  // Lojas distintas vindas do backend (só populado quando consolidar=true) —
  // define se o gráfico de Faturamento vira "uma linha por loja".
  const lojasFat = useMemo(() => {
    const map = new Map();
    vendasDiaFmt.forEach(d => { if (d.organizacao_id != null) map.set(d.organizacao_id, d.organizacao_nome); });
    return [...map.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.id - b.id);
  }, [vendasDiaFmt]);
  const multiLojaFat = consolidar && lojasFat.length > 1;

  // Pivota uma linha por dia+loja pra uma linha por dia com uma coluna
  // `loja_<id>` por loja, formato que o Recharts usa pra desenhar várias
  // linhas na mesma área.
  const vendasDiaPivot = useMemo(() => {
    if (!multiLojaFat) return vendasDiaFmt;
    const porDia = new Map();
    vendasDiaFmt.forEach(d => {
      if (!porDia.has(d.data)) porDia.set(d.data, { data: d.data });
      porDia.get(d.data)[`loja_${d.organizacao_id}`] = d.total;
    });
    return [...porDia.values()].sort((a, b) => a.data.localeCompare(b.data));
  }, [vendasDiaFmt, multiLojaFat]);
  const produtosQtdBar = topProdutos.map(p => ({
    ...p, quantidade_vendida: parseFloat(p.quantidade_vendida) || 0, nomeCurto: nomeCurto(p.nome),
  }));
  const produtosValorBar = topProdutosValor.map(p => ({
    ...p, total_vendido: parseFloat(p.total_vendido) || 0, nomeCurto: nomeCurto(p.nome),
  }));
  const clientesBar = topClientes.map(c => ({
    ...c, total_comprado: parseFloat(c.total_comprado) || 0, nomeCurto: nomeCurto(c.nome),
  }));
  const vendedoresBar = vendedores.map(v => ({ ...v, total_vendido: parseFloat(v.total_vendido) || 0 }));
  const pagamentosPie = formasPagamento.map(f => ({ ...f, total: parseFloat(f.total) || 0 }));
  const totalPagamentos = pagamentosPie.reduce((s, f) => s + f.total, 0);

  const cp = resumo?.contas_pagar;
  const cr = resumo?.contas_receber;

  return (
    <div>
      <BannerNotasTecnicas />
      <h2 className={styles.titulo}>Dashboard</h2>

      <div className={styles.cards}>
        <CardKpi
          titulo="Faturamento Hoje" icone="💰" cor="#1565FF"
          valor={resumo ? formatBRL(resumo.faturamento_hoje) : '—'}
          sub={resumo ? `Ticket médio ${formatBRL(resumo.ticket_medio_hoje)}` : null}
          tendenciaPct={resumo?.variacao_faturamento_pct}
        />
        <CardKpi
          titulo="Vendas Hoje" icone="🛒" cor="#27AE60"
          valor={resumo?.vendas_hoje ?? '—'}
          sub="concluídas"
        />
        <CardKpi
          titulo="A Receber" icone="📥" cor="#32B0FF" to="/financeiro/contas-receber"
          valor={cr ? formatBRL(cr.vencendo_hoje_valor + cr.vencidas_valor) : '—'}
          sub={cr ? `${cr.vencendo_hoje_qtd} vencendo hoje` : null}
          tendenciaPct={null}
        />
        {cr && (
          <CardKpi
            titulo="Recebíveis Vencidos" icone="⚠️" cor={cr.vencidas_qtd > 0 ? '#dc2626' : '#9CA3AF'} to="/financeiro/contas-receber"
            valor={cr.vencidas_qtd}
            sub={cr.vencidas_qtd > 0 ? formatBRL(cr.vencidas_valor) : 'nenhum'}
          />
        )}
        <CardKpi
          titulo="A Pagar" icone="📤" cor="#d97706" to="/financeiro/contas-pagar"
          valor={cp ? formatBRL(cp.vencendo_hoje_valor + cp.vencidas_valor) : '—'}
          sub={cp ? `${cp.vencendo_hoje_qtd} vencendo hoje` : null}
        />
        <CardKpi
          titulo="Alertas de Estoque" icone="📦" cor={resumo?.alertas_estoque > 0 ? '#dc2626' : '#9CA3AF'} to="/estoque"
          valor={resumo?.alertas_estoque ?? '—'}
          sub="abaixo do mínimo"
        />
        <CardKpi
          titulo="Produtos a Vencer" icone="📅" cor={resumo?.produtos_a_vencer > 0 ? '#dc2626' : '#9CA3AF'}
          valor={resumo?.produtos_a_vencer ?? '—'}
          sub="próximos 30 dias" onClick={() => setModalVencer(true)}
        />
        <CardKpi
          titulo="Total de Clientes" icone="👥" cor="#9c27b0" to="/clientes"
          valor={resumo?.total_clientes ?? '—'}
        />
        <CardKpi
          titulo="Produtos Ativos" icone="🏷️" cor="#0D47A1" to="/produtos"
          valor={resumo?.total_produtos ?? '—'}
        />
      </div>

      <div className={styles.graficos}>
        <div className={styles.grafico}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <h3 style={{ margin:0 }}>
              Faturamento — Últimos 30 dias
              {multiLojaFat && <span style={{ fontWeight:'normal', fontSize:12.5, color:'#666' }}> — por loja</span>}
            </h3>
            {podeConsolidar && (
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, cursor:'pointer' }}
                title="Mostra uma linha por loja, em vez de somar tudo numa linha só">
                <input type="checkbox" checked={consolidar} onChange={e => setConsolidar(e.target.checked)} />
                Consolidar todas as lojas
              </label>
            )}
          </div>
          <ResponsiveContainer width="100%" height={260}>
            {multiLojaFat ? (
              // Mesmo padrão visual do gráfico de uma loja só (curva suave +
              // área com gradiente até a base) — antes virava um LineChart
              // (linha fina, sem preenchimento) assim que tinha mais de uma
              // loja, bem menos legível. Uma área com gradiente por loja,
              // cada uma na cor fixa dela (CORES_LOJA, mesma ordem sempre).
              <AreaChart data={vendasDiaPivot}>
                <defs>
                  {lojasFat.map((loja, i) => {
                    const cor = CORES_LOJA[i % CORES_LOJA.length];
                    return (
                      <linearGradient key={loja.id} id={`gradLoja${loja.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={cor} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={cor} stopOpacity={0} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="data" tickFormatter={(d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} width={55} />
                <Tooltip content={<TooltipMoeda />} labelFormatter={(l) => `Data: ${l.slice(8, 10)}/${l.slice(5, 7)}/${l.slice(0, 4)}`} />
                <Legend wrapperStyle={{ fontSize: 12.5 }} />
                {lojasFat.map((loja, i) => (
                  <Area key={loja.id} type="monotone" dataKey={`loja_${loja.id}`} name={loja.nome}
                    stroke={CORES_LOJA[i % CORES_LOJA.length]} strokeWidth={2.5}
                    fill={`url(#gradLoja${loja.id})`} dot={false} activeDot={{ r: 4 }} />
                ))}
              </AreaChart>
            ) : (
              <AreaChart data={vendasDiaFmt}>
                <defs>
                  <linearGradient id="gradFaturamento" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1565FF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1565FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="data" tickFormatter={(d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} width={55} />
                <Tooltip content={<TooltipMoeda />} labelFormatter={(l) => `Data: ${l.slice(8, 10)}/${l.slice(5, 7)}/${l.slice(0, 4)}`} />
                <Area type="monotone" dataKey="total" name="Faturamento" stroke="#1565FF" strokeWidth={2.5}
                  fill="url(#gradFaturamento)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className={styles.grafico}>
          <h3>Formas de Pagamento — 30 dias</h3>
          {pagamentosPie.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pagamentosPie} dataKey="total" nameKey="forma" innerRadius={55} outerRadius={90}
                  paddingAngle={2}>
                  {pagamentosPie.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${formatBRL(v)} (${totalPagamentos > 0 ? ((v / totalPagamentos) * 100).toFixed(1) : 0}%)`, n]} />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.semDados}>Sem vendas nos últimos 30 dias.</p>
          )}
        </div>
      </div>

      <div className={styles.graficos}>
        <div className={styles.grafico}>
          <h3>Top 10 Produtos por Quantidade — 30 dias</h3>
          {produtosQtdBar.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={produtosQtdBar} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nomeCurto" width={170} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatNum(v), 'Quantidade']} labelFormatter={() => ''} />
                <Bar dataKey="quantidade_vendida" name="Quantidade" fill="#1565FF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.semDados}>Sem vendas nos últimos 30 dias.</p>
          )}
        </div>

        <div className={styles.grafico}>
          <h3>Top 10 Produtos por Valor Vendido — 30 dias</h3>
          {produtosValorBar.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={produtosValorBar} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nomeCurto" width={170} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatBRL(v), 'Total vendido']} labelFormatter={() => ''} />
                <Bar dataKey="total_vendido" name="Total vendido" fill="#0D47A1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.semDados}>Sem vendas nos últimos 30 dias.</p>
          )}
        </div>
      </div>

      <div className={styles.graficos}>
        <div className={styles.grafico}>
          <h3>Top 10 Clientes — 30 dias</h3>
          {clientesBar.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={clientesBar} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nomeCurto" width={170} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatBRL(v), 'Total comprado']} labelFormatter={() => ''} />
                <Bar dataKey="total_comprado" name="Total comprado" fill="#9c27b0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.semDados}>Sem clientes identificados nas vendas dos últimos 30 dias.</p>
          )}
        </div>

        <div className={styles.grafico}>
          <h3>Top Vendedores — 30 dias</h3>
          {vendedoresBar.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={vendedoresBar} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatBRL(v), 'Total vendido']} labelFormatter={() => ''} />
                <Bar dataKey="total_vendido" name="Total vendido" fill="#27AE60" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.semDados}>Sem vendas com vendedor identificado nos últimos 30 dias.</p>
          )}
        </div>
      </div>

      {modalVencer && <ModalProdutosAVencer onClose={() => setModalVencer(false)} />}
    </div>
  );
}

// ── Modal "Produtos a Vencer" (Pontos Críticos) ──────────────────────────
// Filtro "dias <= X" — padrão 30, inclui lotes JÁ VENCIDOS (dias negativos)
// de propósito: são o caso mais crítico, não faz sentido escondê-los de um
// relatório que existe justamente pra chamar atenção pra vencimento.
function ModalProdutosAVencer({ onClose }) {
  const [dias, setDias] = useState('30');
  const [diasAplicado, setDiasAplicado] = useState(30);
  const [modalExportar, setModalExportar] = useState(false);
  const { data: lista, isFetching } = useQuery({
    queryKey: ['produtos-a-vencer', diasAplicado],
    queryFn: () => api.get('/dashboard/produtos-a-vencer', { params: { dias: diasAplicado } }).then(r => r.data),
  });

  const aplicarFiltro = (e) => {
    e.preventDefault();
    const v = parseInt(dias);
    setDiasAplicado(Number.isFinite(v) ? v : 30);
  };

  const fmtDataVenc = (d) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 8, width: 'min(900px, 94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,.4)' }}>
        <div style={{ background: '#0f2557', color: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>📅 Produtos a Vencer</strong>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
        <form onSubmit={aplicarFiltro} style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'flex-end', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Vencendo em até quantos dias</label>
            <input type="number" value={dias} onChange={e => setDias(e.target.value)}
              style={{ width: 90, height: 30, padding: '4px 8px', border: '1px solid #94a3b8', borderRadius: 4 }} />
          </div>
          <button type="submit" style={{ height: 30, padding: '0 14px', border: '1px solid #0f2557', borderRadius: 4, background: '#1565c0', color: '#fff', fontSize: 13, cursor: 'pointer' }}>
            Filtrar
          </button>
          <span style={{ fontSize: 12, color: '#666', marginLeft: 4, flex: 1 }}>
            Mostrando até {diasAplicado} dia(s) — inclui já vencidos
          </span>
          <button type="button" onClick={() => setModalExportar(true)} disabled={!lista?.length}
            style={{ height: 30, padding: '0 14px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', color: '#333', fontSize: 12.5, cursor: 'pointer' }}>
            📤 Exportar / Imprimir
          </button>
        </form>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Cód.</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Descrição</th>
                <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Qtd. Estoque</th>
                <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Preço Unit.</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Vencimento</th>
                <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Dias p/ vencer</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#888' }}>Carregando…</td></tr>
              )}
              {!isFetching && lista?.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#888', fontStyle: 'italic' }}>
                  Nenhum produto vencendo nesse período.
                </td></tr>
              )}
              {!isFetching && lista?.map(p => {
                const dp = parseInt(p.dias_para_vencer);
                const cor = dp < 0 ? '#b91c1c' : dp <= 7 ? '#c2410c' : dp <= 30 ? '#92400e' : '#333';
                return (
                  <tr key={p.id} style={{ background: dp < 0 ? '#fee2e2' : dp <= 30 ? '#fff7ed' : 'white' }}>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee', fontFamily: 'monospace' }}>{p.codigo_interno}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{p.descricao}{p.numero_lote ? ` (Lote ${p.numero_lote})` : ''}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee', textAlign: 'right' }}>{Number(p.estoque_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee', textAlign: 'right' }}>{formatBRL(p.preco_venda)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{fmtDataVenc(p.data_vencimento)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 700, color: cor }}>
                      {dp < 0 ? `Vencido há ${Math.abs(dp)}d` : `${dp}d`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalExportar && (
        <ModalExportarLista
          titulo="Produtos a Vencer"
          colunas={COLUNAS_EXPORT_VENCER}
          dados={lista || []}
          nomeArquivo={`produtos_a_vencer_${new Date().toISOString().slice(0, 10)}`}
          onClose={() => setModalExportar(false)}
        />
      )}
    </div>
  );
}
